export interface DiffRunFormData {
  linkedAccountId: string;
  disableFilterByDate: boolean;
  maxPageIterations?: number;
  maxLoopIterations?: number;
  overrideLastRunAt?: string;
  respectLinkedAccountScopes: boolean;
}

export enum CommonModelDiffRunStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  SUCCEEDED = "SUCCEEDED",
  FAILED = "FAILED",
  ABORTED = "ABORTED",
}

export interface DiffExecutionModelResult {
  model_name: string;
  staged_blueprint_version_model_count: number;
  published_blueprint_version_model_count: number;
  different_fields?: string[];
  number_of_different_models?: number;
  number_of_net_missing_models?: number;
  number_of_net_new_models?: number;
  number_of_same_models?: number;
}

export interface DiffExecutionResults {
  models?: DiffExecutionModelResult[];
  staged_blueprint_version_api_requests?: number;
  published_blueprint_version_api_requests?: number;
  total_staged_blueprint_version_model_count?: number;
  total_published_blueprint_version_model_count?: number;
  total_different_models?: number;
  total_net_new_models?: number;
  total_net_missing_models?: number;
  total_same_models?: number;
}

// Running-phase of a diff, surfaced by the backend while status is PENDING/IN_PROGRESS.
// Null once terminal (use `status` there). Kept in sync with the backend CMDPhase literal.
export type CommonModelDiffPhase =
  | "starting"
  | "published_running"
  | "published_done"
  | "staged_running"
  | "diffing";

// Slim view of one blueprint sub-run of a diff (published or staged). async_task_execution_id
// is what the console's step-I/O log viewer loads (GET /blueprints/async-test-run/{id}).
export interface CommonModelDiffSubRun {
  async_task_execution_id: string;
  blueprint_execution_id?: string | null;
  exit_code?: number | null;
  has_started_execution?: boolean;
  exception_message?: string | null;
}

// Opens the console step-I/O log viewer for one sub-run. Optional: only wired in the
// blueprint-editor console drawer (the staged-versions tab has no viewer to open into).
export type ViewExecutionLogsHandler = (asyncTaskExecutionId: string) => void;

export interface CommonModelDiffRunExecution {
  id: string;
  linked_account_id: string;
  staged_blueprint_version_id: string;
  published_blueprint_version_id: string;
  linked_account_name: string;
  status: CommonModelDiffRunStatus;
  results: DiffExecutionResults;
  created_at: string;
  is_acknowledged: boolean;
  end_time?: string;
  exception_message?: string;
  phase?: CommonModelDiffPhase | null;
  published_run?: CommonModelDiffSubRun | null;
  staged_run?: CommonModelDiffSubRun | null;
}

export enum DiffCategoryTypes {
  DIFFERENT = "DIFFERENT",
  NET_MISSING = "NET_MISSING",
  NET_NEW = "NET_NEW",
  SAME = "SAME",
}

export interface FieldData {
  [key: string]: string | boolean | null;
}

export interface DiffFieldValues {
  diffed_fields: FieldData;
  published_fields: FieldData;
  staged_fields: FieldData;
}

export interface IndividualDiffResult {
  field_values: DiffFieldValues;
  diff_category: DiffCategoryTypes;
}

export interface CommonModelDiffResult {
  results: IndividualDiffResult[];
  last_key: any | null;
}
